
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback } from 'react';
import { RotateCcwIcon, SaveIcon, ShareIcon, BookOpenIcon, SunIcon, MoonIcon, SearchIcon, MessageSquareIcon, SparklesIcon, UsersIcon, PlusIcon, ShoppingBagIcon, LayersIcon, MenuIcon, XIcon, PanelLeftIcon, PanelRightIcon, GoogleIcon, FacebookIcon, LockIcon } from './icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { useLanguage } from '../i18n/useLanguage';
import { useAppContext } from '../AppContext';
import { useOutfit } from '../hooks/useOutfit';
import { AppPage } from '../types';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';
import { getUserProfile, saveUserProfile, isEmailRegistered, hasAssistantAccess } from '@shared/services/userService';
import { hashPassword } from '@shared/services/cryptoService';
import { createRegistrationProfile } from '../services/registrationService';
import AuthModal from './AuthModal';

interface NavBarProps {
  onStartOver: () => void;
  isLoading: boolean;
  onSaveOutfit: () => void;
  onShareOutfit: () => void;
  onShowLookbookCreator: () => void;
  onFindSimilarItems: () => void;
  onGetStyleTips: () => void;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  onShowBasket: () => void;
  onShowAgentChat?: () => void;
  onToggleLeftPanel: () => void;
  isLeftPanelOpen: boolean;
  onToggleRightPanel: () => void;
  isRightPanelOpen: boolean;
  basketItemCount: number;
  handleUserChangeRequest: (newUserId: string) => Promise<void>;
  isStudioPage?: boolean;
}

const mainNavLinks: { page?: AppPage; labelKey: string; guestAccess: boolean; children?: { page: AppPage; labelKey: string; guestAccess: boolean; params?: Record<string, string> }[] }[] = [
  { page: 'style-lab', labelKey: 'nav_style_lab', guestAccess: false },
  {
    labelKey: 'nav_fashion',
    guestAccess: true,
    children: [
      { page: 'travel', labelKey: 'nav_travel', guestAccess: true },
      { page: 'my-styles', labelKey: 'nav_outfits', guestAccess: false, params: { tab: 'outfits' } },
      { page: 'my-styles', labelKey: 'nav_lookbooks', guestAccess: false, params: { tab: 'lookbooks' } },
      { page: 'my-styles', labelKey: 'nav_color_studio', guestAccess: true, params: { tab: 'color_analysis' } },
      { page: 'capsule-generator', labelKey: 'nav_capsule_generator', guestAccess: true },
    ]
  },
  { page: 'styling-advisor', labelKey: 'nav_advisor', guestAccess: true },
  { page: 'services', labelKey: 'nav_services', guestAccess: true },
  { page: 'portfolio', labelKey: 'nav_portfolio', guestAccess: true },
  { page: 'booking', labelKey: 'nav_bookSession', guestAccess: true },
  { page: 'blog', labelKey: 'nav_blog', guestAccess: true },
  { page: 'trends', labelKey: 'nav_trends', guestAccess: true },
];

const NavBar: React.FC<NavBarProps> = ({
  onStartOver,
  isLoading,
  onSaveOutfit,
  onShareOutfit,
  onShowLookbookCreator,
  onFindSimilarItems,
  onGetStyleTips,
  isDarkMode,
  onToggleDarkMode,
  onShowBasket,
  onToggleLeftPanel,
  isLeftPanelOpen,
  onToggleRightPanel,
  isRightPanelOpen,
  basketItemCount,
  handleUserChangeRequest,
  isStudioPage,
  onShowAgentChat
}) => {
  const {
    currentUserId, allUsers, setAllUsers, userProfile, setCurrentUserId
  } = useAppContext();
  const { baseModelImageUrl } = useOutfit();

  const { t } = useTranslation();
  const { lang, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = (location.pathname.split('/')[1] || 'home') as AppPage;

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const REMEMBERED_USER_KEY = 'rememberedUserId';
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return !!localStorage.getItem(REMEMBERED_USER_KEY);
    } catch (e) {
      return false;
    }
  });

  const handleUserChange = useCallback(async (user: string) => {
    await handleUserChangeRequest(user);
    if (rememberMe) {
      localStorage.setItem(REMEMBERED_USER_KEY, user);
    } else {
      localStorage.removeItem(REMEMBERED_USER_KEY);
    }
    setShowUserDropdown(false);

    // If logging out (switching to guest), force refresh to clear all app state
    if (user === 'guest-user') {
      window.location.href = '/';
    }
  }, [handleUserChangeRequest, rememberMe]);

  const handleEmailAuth = useCallback(async (email: string, isSignup: boolean, password?: string) => {
    const name = email.split('@')[0];
    const userId = `${name}-user`;

    if (isSignup) {
      if (await isEmailRegistered(email)) {
        // This is now also handled in AuthModal, but good to have here as well
        throw new Error(t('auth_error_emailExists'));
      }

      const newProfile = createRegistrationProfile(email) as any;
      if (password) {
        newProfile.passwordHash = await hashPassword(password);
      }
      await saveUserProfile(userId, newProfile);

      if (!allUsers.includes(userId)) {
        setAllUsers([...allUsers, userId]);
      }
      // Switch to the newly registered (but pending) user
      await handleUserChange(userId);

      // Redirect to Service Selection Wizard
      onNavigate('service-selection');
    } else {
      // Login
      const { isExternalBackend, apiClient } = await import('@shared/services/apiClient');
      if (isExternalBackend()) {
        try {
          const response = await apiClient.post<{ success: boolean, token: string, user: any }>('/api/v1/auth/login', { userId, password });
          if (response.success && response.token) {
            localStorage.setItem('auth_token', response.token);
          } else {
            throw new Error('Authentication failed');
          }
        } catch (error: any) {
          throw new Error(t('auth_error_invalid_credentials') || 'Invalid credentials');
        }
      } else {
        if (email.toLowerCase().startsWith('admin')) {
          const { UIX_CONFIG } = await import('../config');
          if (password !== UIX_CONFIG.ADMIN_PASSWORD) {
            throw new Error(t('auth_error_invalid_credentials') || 'Invalid credentials');
          }
        } else {
          // Regular user login checks
          const profile = await getUserProfile(userId);
          if (profile && profile.email) {
            if (password && profile.passwordHash) {
              const hashedInput = await hashPassword(password);
              if (profile.passwordHash !== hashedInput) {
                throw new Error(t('auth_error_invalid_credentials') || 'Invalid credentials');
              }
            }
          } else {
             // If profile does not exist or email is not set, we shouldn't approve login implicitly here,
             // although below code checks allUsers. Let's let the below logic handle the missing profile error.
          }
        }
      }

      if (allUsers.includes(userId)) {
        await handleUserChange(userId);
      } else {
        // If profile exists in storage but not in allUsers state
        const profile = await getUserProfile(userId);
        if (profile.email) {
          if (!allUsers.includes(userId)) {
            setAllUsers([...allUsers, userId]);
          }
          await handleUserChange(userId);
        } else {
          // If it's an admin and correctly authenticated but not in allUsers yet
          if (email.toLowerCase().startsWith('admin')) {
            if (!allUsers.includes(userId)) {
              setAllUsers([...allUsers, userId]);
            }
            await handleUserChange(userId);
          } else {
            throw new Error(t('agentWorkflow_error'));
          }
        }
      }
    }
  }, [allUsers, setAllUsers, t, handleUserChange]);

  const openAuthModal = () => {
    setShowUserDropdown(false);
    setIsAuthModalOpen(true);
  };

  const navItemClass = "flex items-center text-sm font-medium py-2 px-3 rounded-lg transition-all duration-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed";
  const activeNavItemClass = "text-indigo-600 dark:text-indigo-400 font-semibold bg-indigo-50/50 dark:bg-indigo-900/20";
  const defaultNavItemClass = "text-gray-600 dark:text-gray-300";
  const buttonClass = "flex items-center justify-center text-center bg-white/50 border border-gray-200/50 text-gray-700 font-medium py-2 px-4 rounded-full transition-all duration-300 hover:bg-white hover:shadow-md active:scale-95 text-sm backdrop-blur-sm dark:bg-gray-800/50 dark:border-gray-700/50 dark:text-gray-200 hover:dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed";
  const startOverButtonClass = "flex items-center justify-center text-center bg-white/50 border border-gray-200/50 text-gray-700 font-medium py-2 px-4 rounded-full transition-all duration-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200 hover:shadow-md active:scale-95 text-sm backdrop-blur-sm dark:bg-gray-800/50 dark:border-gray-700/50 dark:text-gray-200 hover:dark:bg-red-900/20 hover:dark:text-red-400 hover:dark:border-red-800 disabled:opacity-50 disabled:cursor-not-allowed";

  const arePrerequisitesMet = userProfile.stylePersona && userProfile.bodySize;
  const hasPremiumAccess = userProfile.membershipLevel === 'premium' || userProfile.membershipLevel === 'advanced';
  const isGuest = currentUserId.includes('guest');
  const isModelLoaded = !!baseModelImageUrl;
  const isAssistantLocked = !hasAssistantAccess(userProfile);

  const onNavigate = (page: AppPage, params?: Record<string, string>) => {
    let url = `/${page}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    navigate(url);
  };

  const renderNavLinks = (isMobile: boolean) => (
    mainNavLinks.map(link => {
      if (link.children) {
        const isDropdownActive = activeDropdown === link.labelKey;

        return (
          <div key={link.labelKey} className="relative group/dropdown">
            <button
              onClick={() => {
                if (isMobile) {
                  setActiveDropdown(isDropdownActive ? null : link.labelKey);
                }
              }}
              className={`${navItemClass} w-full justify-start ${isMobile ? 'text-lg p-4' : ''} ${defaultNavItemClass} flex justify-between items-center`}
              onMouseEnter={() => !isMobile && setActiveDropdown(link.labelKey)}
              onMouseLeave={() => !isMobile && setActiveDropdown(null)}
            >
              {t(link.labelKey)}
              <span className={`transform transition-transform ${isDropdownActive ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {/* Dropdown Menu */}
            <div
              className={`${isMobile ? (isDropdownActive ? 'block pl-4' : 'hidden') : 'absolute left-0 top-full pt-2 opacity-0 invisible group-hover/dropdown:opacity-100 group-hover/dropdown:visible transition-all duration-200 z-50'}`}
              onMouseEnter={() => !isMobile && setActiveDropdown(link.labelKey)}
              onMouseLeave={() => !isMobile && setActiveDropdown(null)}
            >
              <div className={`${!isMobile ? 'bg-white/90 backdrop-blur-xl border border-gray-200/60 rounded-xl shadow-xl dark:bg-gray-800/90 dark:border-gray-700/60 p-2 min-w-[200px]' : ''}`}>
                {link.children.map(child => (
                  <button
                    key={child.labelKey}
                    onClick={() => {
                      onNavigate(child.page, child.params);
                      if (isMobile) setIsMobileMenuOpen(false);
                      setActiveDropdown(null);
                    }}
                    className={`${navItemClass} w-full justify-start ${isMobile ? 'text-base p-3 border-l-2 border-gray-200 dark:border-gray-700' : ''} ${currentPage === child.page ? activeNavItemClass : defaultNavItemClass}`}
                    disabled={isGuest && !child.guestAccess}
                    title={isGuest && !child.guestAccess ? t('nav_guestCta') : ''}
                  >
                    {t(child.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      }

      return (
        <button
          key={link.page}
          onClick={() => {
            // @ts-ignore
            onNavigate(link.page);
            if (isMobile) setIsMobileMenuOpen(false);
          }}
          className={`${navItemClass} w-full justify-start ${isMobile ? 'text-lg p-4' : ''} ${(currentPage === link.page || (link.page === 'style-lab' && isStudioPage)) ? activeNavItemClass : defaultNavItemClass}`}
          disabled={isGuest && !link.guestAccess}
          title={isGuest && !link.guestAccess ? t('nav_guestCta') : ''}
        >
          {t(link.labelKey)}
        </button>
      )
    })
  );

  return (
    <>
      <nav className="w-full bg-white/70 dark:bg-gray-900/70 backdrop-blur-2xl border-b border-gray-200/40 dark:border-gray-700/40 sticky top-0 z-50 transition-all duration-300">
        <div className="mx-auto flex items-center justify-between gap-4 max-w-[1400px] px-6 py-3">
          <div className="flex items-center gap-6">
            <button onClick={() => onNavigate('home')} className="flex items-center gap-2 flex-shrink-0 focus:outline-none group" title={t('nav.home')}>
              <Logo className="h-9 w-auto text-gray-900 dark:text-white transition-transform group-hover:scale-105" />
            </button>
            <div className="hidden md:flex items-center gap-1">
              {renderNavLinks(false)}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center lg:justify-end gap-3">
            {isStudioPage && isModelLoaded && (
              <div className="hidden lg:flex items-center gap-2 mr-4 border-r border-gray-200/50 dark:border-gray-700/50 pr-4">
                <button onClick={onToggleLeftPanel} className={`${buttonClass} !p-2`} title={isLeftPanelOpen ? t('nav_hide_wardrobe') : t('nav_show_wardrobe')}>
                  <PanelLeftIcon className="w-5 h-5" />
                </button>
                <button onClick={onStartOver} className={startOverButtonClass} disabled={isLoading}>
                  <RotateCcwIcon className="w-4 h-4 mr-2" /> {t('nav_startOver')}
                </button>
                <button onClick={onSaveOutfit} className={buttonClass} disabled={isLoading || isGuest} title={isGuest ? t('nav_guestCta') : ''}>
                  <SaveIcon className="w-4 h-4 mr-2" /> {t('nav_save')}
                </button>
                <button onClick={onShareOutfit} className={buttonClass} disabled={isLoading || !hasPremiumAccess} title={!hasPremiumAccess ? t('feature_locked_premium_generic') : t('nav_share')}>
                  <ShareIcon className="w-4 h-4 mr-2" /> {t('nav_share')}
                </button>
                <button onClick={onFindSimilarItems} className={buttonClass} disabled={isLoading || !hasPremiumAccess} title={!hasPremiumAccess ? t('feature_locked_premium_generic') : t('nav_findSimilar')}>
                  <SearchIcon className="w-4 h-4 mr-2" /> {t('nav_findSimilar')}
                </button>
                <button onClick={onGetStyleTips} className={buttonClass} disabled={isLoading || !arePrerequisitesMet || !hasPremiumAccess} title={!arePrerequisitesMet ? t('stylingAdvisorPage_mandatory_fields_tooltip') : !hasPremiumAccess ? t('feature_locked_premium_generic') : t('nav_getStyleTip')}>
                  <MessageSquareIcon className="w-4 h-4 mr-2" /> {t('nav_getStyleTip')}
                </button>
                <button onClick={onToggleRightPanel} className={`${buttonClass} !p-2`} title={isRightPanelOpen ? t('nav_hide_outfit') : t('nav_show_outfit')}>
                  <PanelRightIcon className="w-5 h-5" />
                </button>
                <button onClick={onShowAgentChat} className={`${buttonClass} !bg-indigo-600 !text-white hover:!bg-indigo-700`} title={t('nav_advisor')}>
                  <SparklesIcon className="w-5 h-5" />
                </button>
              </div>
            )}
            {isStudioPage && (<button onClick={onShowLookbookCreator} className={buttonClass} disabled={isLoading || isGuest || userProfile.membershipLevel === 'basic'} title={isGuest ? t('nav_guestCta') : userProfile.membershipLevel === 'basic' ? t('feature_locked_premium_generic') : t('nav_lookbook')}>
              <BookOpenIcon className="w-4 h-4 mr-2" /> {t('nav_lookbook')}
            </button>)}
            <div className="h-6 w-px bg-gray-200/50 dark:bg-gray-700/50 hidden md:block"></div>
            <LanguageSwitcher />
            <button onClick={onToggleDarkMode} className={`${buttonClass} !px-3`} aria-label={isDarkMode ? t('nav_lightMode') : t('nav_darkMode')} title={isDarkMode ? t('nav_lightMode') : t('nav_darkMode')}>
              {isDarkMode ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
            </button>
            <button onClick={onShowBasket} className={`${buttonClass} relative !px-3`} aria-label={t('navbar_shopping_basket_aria')} title={t('navbar_shopping_basket_aria')}>
              <ShoppingBagIcon className="w-5 h-5" />
              {basketItemCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow-sm">
                  {basketItemCount}
                </span>
              )}
            </button>
            <div className="relative">
              {isGuest ? (
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="flex items-center justify-center bg-gray-900 text-white text-sm font-bold py-2 px-4 rounded-full transition-all duration-300 hover:bg-black hover:shadow-lg active:scale-95 dark:bg-indigo-600 dark:hover:bg-indigo-500 ring-2 ring-indigo-500/20"
                  aria-label={t('nav_signUp')}
                  title={t('nav_signUp')}
                >
                  <UsersIcon className="w-4 h-4 mr-2" />
                  {t('nav_signUp')}
                </button>
              ) : (
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className={`${buttonClass} pl-3 pr-4 py-2 border-transparent bg-indigo-50/50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40`}
                  aria-label={t('nav_switchUser')}
                  title={t('nav_switchUser')}
                >
                  <div className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center mr-2 text-indigo-700 dark:text-indigo-300">
                    <UsersIcon className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-xs font-semibold tracking-wide">{currentUserId.split('-')[0].charAt(0).toUpperCase() + currentUserId.split('-')[0].slice(1)}</span>
                </button>
              )}
              {showUserDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 w-72 bg-white/90 backdrop-blur-xl border border-gray-200/60 rounded-2xl shadow-2xl dark:bg-gray-800/90 dark:border-gray-700/60 z-50 p-3 ring-1 ring-black/5"
                >
                  {!isGuest && (
                    <button
                      onClick={() => { onNavigate('profile'); setShowUserDropdown(false); }}
                      className="block w-full text-left px-4 py-2.5 text-sm rounded-xl font-medium text-gray-800 hover:bg-gray-100/80 dark:text-gray-200 dark:hover:bg-gray-700/80 transition-colors"
                    >
                      {t('nav_profile')}
                    </button>
                  )}
                  <button
                    onClick={() => { onNavigate('help'); setShowUserDropdown(false); }}
                    className="block w-full text-left px-4 py-2.5 text-sm rounded-xl font-medium text-gray-800 hover:bg-gray-100/80 dark:text-gray-200 dark:hover:bg-gray-700/80 transition-colors"
                  >
                    {t('nav_help')}
                  </button>
                  <div className="border-t border-gray-200/50 dark:border-gray-600/50 my-2"></div>
                  <div className="max-h-48 overflow-y-auto custom-scrollbar mb-2 px-1">
                    {allUsers.map((user) => (
                      <button
                        key={user}
                        onClick={() => handleUserChange(user)}
                        className={`block w-full text-left px-4 py-2 text-sm rounded-xl transition-all ${currentUserId === user
                          ? 'bg-indigo-50 text-indigo-700 font-semibold dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
                          }`}
                      >
                        {user.split('-')[0].charAt(0).toUpperCase() + user.split('-')[0].slice(1)} {user.includes('guest') ? t('nav_guestUser') : ''}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-gray-200/50 dark:border-gray-600/50 my-2"></div>
                  <div className="px-3 py-1">
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none group">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="peer h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-900 dark:border-gray-600 transition-all cursor-pointer"
                        />
                      </div>
                      <span className="ml-2 group-hover:text-indigo-600 transition-colors">{t('nav_rememberMe')}</span>
                    </label>
                  </div>

                  {!isGuest && (
                    <div className="border-t border-gray-200/50 pt-3 dark:border-gray-700/50 mt-2">
                      <button
                        onClick={() => { handleUserChange('guest-user'); setShowUserDropdown(false); }}
                        className="w-full flex items-center justify-center text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 px-4 py-2 text-sm rounded-xl font-medium transition-colors"
                      >
                        {t('nav_logout')}
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
            <div className="md:hidden">
              <button onClick={() => setIsMobileMenuOpen(true)} className={`${buttonClass} !px-3`} title="Menu">
                <MenuIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav >
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: 'spring', stiffness: 300, damping: 35 }}
              className="absolute top-0 left-0 h-full w-[85%] max-w-sm bg-white dark:bg-gray-900 p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100 dark:border-gray-800">
                <Logo className="h-8 w-auto text-gray-900 dark:text-white" />
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800" title={t('common_close')}>
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="flex flex-col h-full">
                <nav className="flex flex-col gap-2">
                  {renderNavLinks(true)}
                </nav>
                {isStudioPage && isModelLoaded && (
                  <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{t('nav_actions_title')}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={onToggleLeftPanel} className={`${buttonClass} !w-full`}>
                        <ShoppingBagIcon className="w-5 h-5 mr-2" /> {t('nav_wardrobe')}
                      </button>
                      <button onClick={onToggleRightPanel} className={`${buttonClass} !w-full`}>
                        <LayersIcon className="w-5 h-5 mr-2" /> {t('nav_outfit')}
                      </button>
                      <button onClick={onSaveOutfit} className={`${buttonClass} !w-full`}>
                        <SaveIcon className="w-5 h-5 mr-2" /> {t('nav_save')}
                      </button>
                      <button onClick={onShareOutfit} className={`${buttonClass} !w-full`}>
                        <ShareIcon className="w-5 h-5 mr-2" /> {t('nav_share')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onEmailAuth={handleEmailAuth}
      />
    </>
  );
};

export default NavBar;
